import { HighlightedRequest } from '@components/highlightedRequest/highlightedRequest';
import { Location } from '@components/location/location';
import { useIsVenue } from '@hooks/use-is-venue';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useAppSelector } from '@redux/hooks';
import { GridColumn } from '@tkww/orion-web-ds-structure';
import { GridRow } from '@tkww/orion-web-ds-structure';
import { TextTitle } from '@tkww/orion-web-ds-text';
import { Img } from '@xo-union/tk-component-picture';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import React, { type FC } from 'react';
import type { Decorated } from 'types/vendor';
import {
	travelAvailabilityAtom,
	travelRangeAtom,
} from '../../../../jotai/storefront/organization';
import type { NavItemProps } from '../nav/types';
import ContactInfo from './components/contact_info';
import Social from './components/social';
import Styles from './styles.scss';

interface VendorInfoProps {
	vendor: Decorated;
	isContactStandAlone: boolean;
}
const VendorInfo: FC<VendorInfoProps> = ({ vendor, isContactStandAlone }) => {
	const isLiteStorefront = useLiteStorefront();
	return (
		<div>
			<ContactInfo isContactStandAlone={isContactStandAlone} />
			<div>
				<Social
					containerClassname={classNames({
						[Styles.contactSocialStandalone]: isContactStandAlone,
					})}
					social={vendor.social}
					vendor={vendor}
					iconSize={isLiteStorefront ? 'md' : 'lg'}
					isFooter
				/>
			</div>
		</div>
	);
};

interface ContactCTAProps {
	useContactCTA: boolean;
}

const ContactCTA: FC<ContactCTAProps> = ({ useContactCTA }) => {
	const isLiteStorefront = useLiteStorefront();

	if (!isLiteStorefront) {
		if (useContactCTA) {
			return (
				<HighlightedRequest
					messageText="Curious to learn more?"
					buttonText="Start a conversation"
					initiator="Contact CTA"
				/>
			);
		}
		return (
			<HighlightedRequest
				messageText="Want to book a visit?"
				buttonText="Start a conversation"
				initiator="Location CTA"
			/>
		);
	}

	return (
		<HighlightedRequest
			messageText="Want to book a visit?"
			buttonText="Start a conversation"
			initiator="Location CTA"
			hideIfLead
		/>
	);
};

const Contact: FC = () => {
	const vendor = useAppSelector((state) => {
		return state.vendor.vendor;
	});
	const isLiteStorefront = useLiteStorefront();
	const logoUrl = vendor?.logo?.url;
	const travelAvailability = useAtomValue(travelAvailabilityAtom);
	const travelRange = useAtomValue(travelRangeAtom);
	const isVenue = useIsVenue();

	const hasNoAddress =
		!vendor?.address?.address &&
		!vendor?.address?.city &&
		!vendor?.address?.state;
	const hasNoServiceArea = !vendor?.serviceArea;
	const isAddressPublic = !!vendor?.address.isPublic;

	if (!vendor || (isLiteStorefront && hasNoAddress && hasNoServiceArea)) {
		return null;
	}

	const isTravelPossible = travelRange || travelAvailability;
	const isLocationHiddenByPrivacy =
		!isAddressPublic && !isTravelPossible && !isVenue;

	const isContactWithoutLocation =
		isLocationHiddenByPrivacy || isLiteStorefront;

	return (
		<div
			className={classNames(Styles.contactInfoSection, {
				[Styles.noDivider]: !isLiteStorefront,
			})}
			id="contact"
		>
			<GridRow
				hGutters={false}
				className={isContactWithoutLocation ? Styles.contactStandalone : ''}
			>
				<GridColumn sm="12" md="8" orderMd="1">
					{isContactWithoutLocation && (
						<TextTitle
							size="sm"
							className={Styles.title}
							data-testid="storefront-contact-header"
						>
							Contact
						</TextTitle>
					)}
					<VendorInfo
						vendor={vendor}
						isContactStandAlone={isContactWithoutLocation}
					/>
				</GridColumn>
				{logoUrl && (
					<GridColumn
						md="4"
						orderMd="2"
						className={classNames(Styles.logo, {
							[Styles.marginTop]: isContactWithoutLocation,
						})}
					>
						<Img
							src={`${logoUrl}~rs_${!isLiteStorefront ? '350.216' : '216.104'}.fit?quality=90`}
							alt="Vendor Logo"
						/>
					</GridColumn>
				)}
			</GridRow>
			{!isLiteStorefront && vendor.claimedStatus === 'CLAIMED' && (
				<GridRow className={Styles.fullWidth}>
					<GridColumn
						sm="12"
						md="12"
						orderMd="1"
						className={Styles.messageVendorCta}
					>
						<ContactCTA useContactCTA={!isAddressPublic} />
					</GridColumn>
				</GridRow>
			)}
		</div>
	);
};

export const ContactLocation: FC<NavItemProps> = () => {
	const isMobile = useAppSelector((state) => state.viewport)?.isMobile;
	const isLiteStorefront = useLiteStorefront();

	return (
		<div className={Styles.contactLocationWrapper}>
			{!isLiteStorefront && (
				<Location
					className={Styles.noDivider}
					forcedTitle={
						!isLiteStorefront
							? isMobile
								? 'Contact info & Service area'
								: 'Service area & Contact info'
							: null
					}
				/>
			)}
			<Contact />
		</div>
	);
};
